<template>
  <div
    class="em-accordion"
    :class="{
      'em-accordion--open': isOpen,
      'em-accordion--disabled': disabled,
      'em-accordion--no-border': !border,
    }"
  >
    <button
      class="em-accordion__header"
      :class="headerClass"
      type="button"
      :aria-expanded="isOpen"
      :disabled="disabled"
      @click="toggle"
    >
      <slot name="header">
        <span class="em-accordion__title">{{ title }}</span>
      </slot>
      <span class="em-accordion__icon" :class="{ 'em-accordion__icon--rotated': isOpen }">
        <slot name="icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 512 512"
            fill="currentColor"
          >
            <path
              d="M256 294.1L383 167c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9l-143 143c-9.4 9.4-24.6 9.4-33.9 0l-143-143c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0l127 127.1z"
            />
          </svg>
        </slot>
      </span>
    </button>
    <div class="em-accordion__body" :class="{ 'em-accordion__body--open': isOpen }">
      <div class="em-accordion__body-inner">
        <div class="em-accordion__content" :class="contentClass">
          <slot></slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';

interface Props {
  title?: string;
  modelValue?: boolean;
  disabled?: boolean;
  border?: boolean;
  headerClass?: string;
  contentClass?: string;
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  modelValue: undefined,
  disabled: false,
  border: false,
  headerClass: '',
  contentClass: '',
});

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
}>();

const isControlled = () => props.modelValue !== undefined;
const internalOpen = ref(false);

const isOpen = ref(props.modelValue ?? false);

watch(
  () => props.modelValue,
  (val) => {
    if (val !== undefined) {
      isOpen.value = val;
    }
  }
);

const toggle = () => {
  if (props.disabled) return;

  const newValue = !isOpen.value;

  if (isControlled()) {
    emit('update:modelValue', newValue);
  } else {
    isOpen.value = newValue;
    internalOpen.value = newValue;
  }
};
</script>

<style scoped>
.em-accordion {
  border: 1px solid var(--ion-input-border-color);
  border-radius: var(--ion-radius-lg);
  background-color: var(--ion-background-color);
  overflow: hidden;
  padding: 5px 0;
}

.em-accordion--no-border {
  border: none;
}

.em-accordion__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0 var(--ion-spacing-md);
  background: var(--ion-background-color);
  color: var(--ion-text-color);
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 400;
  font-family: inherit;
  text-align: left;
  transition: background-color 0.2s ease;
}

.em-accordion__header:hover:not(:disabled) {
  /* background: var(--em-accordion-header-hover-bg, rgba(0, 0, 0, 0.03)); */
}

.em-accordion__header:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.em-accordion__title {
  flex: 1;
  min-width: 0;
}

.em-accordion__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-left: 8px;
  transition: transform 0.3s ease;
  color: var(--em-accordion-icon-color, var(--ion-color-primary, currentColor));
}

.em-accordion__icon--rotated {
  transform: rotate(180deg);
}

.em-accordion__body {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.em-accordion__body--open {
  grid-template-rows: 1fr;
}

.em-accordion__body-inner {
  overflow: hidden;
}

.em-accordion__content {
  padding: 12px 0;
  color: var(--em-accordion-content-color, var(--ion-text-color, inherit));
}

/* Disabled */
.em-accordion--disabled {
  opacity: 0.6;
}
</style>
